import { useMemo } from 'react';

import { PaymentSource } from 'gqlhooks';
import type { IStudentDetail } from 'core/hooks/Students/useStudentDetail';
import {
  buildStudentAlerts,
  IStudentAlertsState,
  IStudentAlertSource,
} from 'core/utils/studentAlerts';

/**
 * The orphan-invoice source state, supplied by the caller's
 * `useAllFakturowniaInvoices` lookup (the same set `OrphanInvoicesBlock` uses).
 * Kept as an argument rather than queried here so the panel and the block share
 * one query and one cache entry (Req 5.5).
 */
export interface IOrphanInvoicesSource extends IStudentAlertSource {}

const PANDADOC_LOST = 'LOST';

/** Coerce a possibly string/null numeric to a finite number (0 fallback). */
const toNumber = (value: number | string | null | undefined): number => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
};

/**
 * React adapter that derives the «Needs attention» alert state for a student.
 *
 * Five of the six categories are computed from the already-loaded
 * `useStudentDetail` data (no second query); the orphan-invoice category is
 * passed in from the caller's `useAllFakturowniaInvoices` lookup so the panel
 * reuses the block's data (Req 5.5). The pure `buildStudentAlerts` does the
 * gating/ordering/unevaluable bookkeeping.
 */
export const useStudentAlerts = (
  student: IStudentDetail | undefined,
  orphanInvoices: IOrphanInvoicesSource,
): IStudentAlertsState =>
  useMemo(() => {
    const agreements = (student?.agreements || []).filter(Boolean);

    let overdueContributions = 0;
    let outstandingInvoices = 0;
    let orphanPayments = 0;
    let pandadocMissingOrLost = 0;

    agreements.forEach((agreement) => {
      if (!agreement) return;

      // «Бесплатный договор» is excluded from debt server-side, so its DEBT
      // categories (overdue взносы + outstanding invoices) are suppressed —
      // but the NON-debt categories below (orphan legacy payments, PandaDoc
      // missing/lost) still apply to it. Scope the flag rather than skipping
      // the whole agreement, else a free contract's legacy payment to delete
      // or a lost PandaDoc contract would vanish from «Needs attention».
      const countDebt = !agreement.freeOfCharge;

      // «Финансирование государством»: only the FINAL invoice is a debt;
      // intermediate installments/invoices are not counted.
      const statePrefinanced = Boolean(agreement.statePrefinanced);

      // Overdue contributions: spec-0004 status is the source of truth — it is
      // derived on read from the linked Fakturownia invoices, so a paid+linked
      // invoice with no provisioned hidden Payment reads «paid», not «overdue».
      // The legacy `!complete && delay>0` (Payment-derived stored flags) is kept
      // only as a fallback when `status` is absent (old backend / partial cache).
      // A state-prefinanced contract owes nothing on intermediate взносы.
      if (countDebt && !statePrefinanced) {
        (agreement.contributions?.edges || []).forEach((edge) => {
          const node = edge?.node;
          if (!node) return;
          const status = (node.status || '').toLowerCase();
          const isOverdue = status
            ? status === 'overdue'
            : !node.complete && toNumber(node.delay) > 0;
          if (isOverdue) {
            overdueContributions += 1;
          }
        });
      }

      // Unpaid invoices — read from the invoices themselves. This used to walk
      // `payment.fakturowniaInvoice`, i.e. the hidden-ledger chain, where an
      // `outstanding > 0` row cannot exist by construction (a Payment is only
      // ever provisioned for a FULLY paid invoice). The alert was therefore
      // silently always 0 while the Invoices tab of the same card listed unpaid
      // documents (ТЗ 0029).
      if (countDebt) {
        (agreement.contributions?.edges || []).forEach((edge) => {
          (edge?.node?.invoices || []).forEach((invoice) => {
            if (!invoice || toNumber(invoice.outstanding) <= 0) return;
            // For state-prefinanced contracts only the final invoice is a debt.
            if (!statePrefinanced || invoice.kind === 'final') {
              outstandingInvoices += 1;
            }
          });
        });
      }

      // Legacy payments still awaiting a decision. NARROW predicate — the same
      // one the block it deep-links to uses. `!payment.contribution` alone was
      // true for EVERY auto-provisioned payment by design (the provisioner sets
      // the link on the invoice, never on the payment), so the alert counted
      // healthy rows and disagreed with the block underneath it.
      (agreement.payments || []).forEach((payment) => {
        if (!payment) return;
        if (
          payment.source === PaymentSource.Operator &&
          !payment.contribution &&
          !payment.fakturowniaInvoice
        ) {
          orphanPayments += 1;
        }
      });

      // PandaDoc: missing (no document) or lost (outcome LOST). Non-debt —
      // applies regardless of the free / prefinanced flags.
      const doc = agreement.pandadocDocument;
      if (!doc || doc.outcome === PANDADOC_LOST) {
        pandadocMissingOrLost += 1;
      }
    });

    // Unsettled accounts payable (student-level), where not repaid.
    const unsettledPayables = (student?.accountsPayables || []).filter(
      (ap) => ap && !ap.repaid,
    ).length;

    const studentReady = Boolean(student);

    return buildStudentAlerts({
      overdueContributions: {
        count: overdueContributions,
        loading: !studentReady,
      },
      outstandingInvoices: {
        count: outstandingInvoices,
        loading: !studentReady,
      },
      orphanInvoices: {
        count: orphanInvoices.count,
        loading: orphanInvoices.loading,
        failed: orphanInvoices.failed,
      },
      orphanPayments: { count: orphanPayments, loading: !studentReady },
      unsettledPayables: { count: unsettledPayables, loading: !studentReady },
      pandadocMissingOrLost: {
        count: pandadocMissingOrLost,
        loading: !studentReady,
      },
    });
  }, [
    student,
    orphanInvoices.count,
    orphanInvoices.loading,
    orphanInvoices.failed,
  ]);
